import _ from 'lodash'
import { MutationTree } from 'vuex'
import { I18nState } from '@/modules/i18n/store'
import Locale from '@/modules/i18n/model/Locale'
import WorldLocale from '@/modules/i18n/model/WorldLocale'
import Message from '@/modules/i18n/model/Message'
import MessagePagination from '@/modules/i18n/model/MessagePagination'
import MessageTranslation from '@/modules/i18n/model/MessageTranslation';
import Vue from 'vue'
import KeyValuePair from '@/modules/core/support/KeyValuePair';

export enum MutationTypes {
    SetWorldLocales = 'setWorldLocales',
    SetLocales      = 'setLocales',
    SetLocale       = 'setLocale',
    SetDefaultLocale = 'setDefaultLocale',
    DeleteLocale    = 'deleteLocale',
    SetLocaleMessage = 'setLocaleMessage',
    SetMessageListSelectedLocale = 'setMessageListSelectedLocale',
    SetLocaleMessageMode = 'setLocaleMessageMode',
    SetMessageListSelectedMessage = 'setMessageListSelectedMessage',
    SetMessageInDraft = 'setMessageInDraft',
    MarkLocaleListFullyLoaded = 'markLocaleListFullyLoaded',
    DeleteLocaleMessage = 'deleteLocaleMessage',
    SetMessageListCurrentPage = 'message/list/currenPage/set',
    SetMessageListCurrentFilters = 'message/list/currentFilters',
    SetMessagePagination = 'message/list/pagination/set',
    UpdateCurrentMessageField = 'message/current/field/update',
    UpdateCurrentMessageTranslationsField = 'message/current/translation/field',
}

export enum MessageListModes {
    None = 'none',
    Editing = 'editing',
    Creating = 'creating',
    Deleting = 'deleting',
}

const mutations: MutationTree<I18nState> = {

    /**
     * Registra en el estado del módulo la lista de configuraciones regionales
     * disponibles para usar por la aplicación.
     */
    [MutationTypes.SetWorldLocales]: (state, payload: { worldLocales: WorldLocale[] }) => {
        if (!_.has(payload, 'worldLocales')) {
            throw new Error('payload no incluye la lista de nombres de localismos a registrar')
        }

        state.worldLocales = payload.worldLocales
    },

    /**
     * Registra en el estado la lista de localismos obtenidos desde el servidor
     * de Artisan
     */
    [MutationTypes.SetLocales]: (state, payload: { locales: Locale[]}) => {
        if (!_.has(payload, 'locales')) {
            throw new Error('payload no incluye la lista de localismos')
        }

        state.locales = payload.locales
    },

    /**
     * Inserta un localismo en la lista actual
     *
     * Si el localismo ya existe en el estado, se sobre-escribirá
     */
    [MutationTypes.SetLocale]: (state, payload: { locale: Locale }) => {
        const localeIndex = _.findIndex(state.locales, { identifier: payload.locale.identifier })
        if (localeIndex >= 0) {
            Vue.set(state.locales, localeIndex, payload.locale)
            return
        }

        state.locales.push(payload.locale)
    },

    /**
     * Registra un localismo por defecto
     */
    [MutationTypes.SetDefaultLocale]: (state, payload: {locale: Locale}) => {
        state.defaultLocale = payload.locale
    },

    /**
     * Registra en el estado el localismo seleccionado en el componente de la lista
     * de mensajes.
     */
    [MutationTypes.SetMessageListSelectedLocale]: (state, payload: {locale: string}) => {
        state.messageList.selectedLocale = payload.locale
    },

    /**
     *
     * @param state
     * @param payload
     */
    [MutationTypes.SetLocaleMessage]: (state, payload: {message: Message}) => {
        // const locale: any = _.find(state.locales, { identifier: payload.message.locale })
        const locale = state.locales.find(l => l.identifier = payload.message.locale.toString())!
        const existingMessageKey = _.findIndex(locale.messages, { key: payload.message.key })

        if (existingMessageKey >= 0) {
            Vue.set(locale.messages, existingMessageKey, payload.message)
            return
        }

        locale.messages.push(payload.message)
    },

    /**
     * Cambia el modo de operación de la lista de traducciones/mensajes de un
     * localismo.
     *
     * Permite indicar que operación esta realizando la lista, si esta
     * editando un mensaje existente o si esta creando un
     * mensaje nuevo.
     */
    [MutationTypes.SetLocaleMessageMode]: (state, payload: { mode: MessageListModes }) => {
        state.messageList.mode = payload.mode
    },

    /**
     * Define que traducción/mensaje existente e la lista de mensaje se encuentra
     * actualmente seleccionada.
     */
    [MutationTypes.SetMessageListSelectedMessage]: (state, payload: { message: Message }) => {
        state.messageList.selectedMessage = payload.message
    },

    /**
     * Elimina del estado un localismo
     */
    [MutationTypes.DeleteLocale]: (state, payload: {locale: string}) => {
        const index = _.findIndex(state.locales, {identifier: payload.locale})
        if (index >= 0) {
            state.locales.splice(index, 1)
        }
    },

    /**
     * Marca si la lista de localismo esta completamente cargada o no
     */
    [MutationTypes.MarkLocaleListFullyLoaded]: (state, isFullyLoaded: boolean) => {
        state.localeListFullyLoaded = isFullyLoaded
    },

    /**
     * Elimina un mensaje de un localismo en el estado
     *
     * @param state
     */
    [MutationTypes.DeleteLocaleMessage]: (state, payload: {locale: string, message: string}) => {
        const locale: Locale | undefined = state.locales.find(l => l.identifier === payload.locale)
        if (!locale) {
            return
        }

        const messageIndex = locale.messages.findIndex(m => m.key === payload.message)
        locale.messages.splice(messageIndex, 1)
    },

    /**
     * Establece la página actual en la paginación de la lista de mesnajes
     */
    [MutationTypes.SetMessageListCurrentPage]: (state, page: number) => state.messageList.currentPage = page,

    /**
     * Establece la paginación de mensajes del servidor mostrada actualmente al usuario
     */
    [MutationTypes.SetMessagePagination]: (state, pagination: MessagePagination) => {
        state.messageList.pagination = pagination
    },

    /**
     * Actualiza un campo del mensaje actualmente en edición
     */
    [MutationTypes.UpdateCurrentMessageField]: (state, payload: {path: string, value: any}) => {
        const message = state.messageList.selectedMessage
        if (!message) {
            throw new Error('No existe ningún mensaje seleccionado')
        }

        _.set(message, payload.path, payload.value)
        state.messageList.selectedMessage = message
    },

    /**
     * Actualiza un campo de una tradicción del mensaje acualmente en edición
     */
    [MutationTypes.UpdateCurrentMessageTranslationsField]:
    (state, payload: {locale: string, path: string, value: any}) => {
        const message = state.messageList.selectedMessage
        if (!message) {
            throw new Error('No existe ningún mensaje seleccionado')
        }

        let translation = message.translate(payload.locale) || new MessageTranslation(0, payload.locale)
        translation = _.set(_.clone(translation), payload.path, payload.value)

        message.addTranslation(translation)
        state.messageList.selectedMessage = _.clone(message)
    },

    [MutationTypes.SetMessageListCurrentFilters]:
    (state, filters: KeyValuePair) => state.messageList.paginationFilters = filters,
}

export default mutations
